import { useState, useEffect, useMemo } from 'react';
import { values, keyBy } from 'lodash';
import { Input, Select, Spinner, Switch, useDisclosure, useToast } from '@chakra-ui/react';
import { useUploadPicturesMutation, TPictures, TFile, TPictureUploadResponse } from 'core/API/picturesApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { isEmpty } from 'utils';
import { isFetchBaseQueryError, isErrorWithMessage } from 'utils/erros';
import RSelect, { formatOptionsForRSelect } from 'components/atoms/formInputs/RSelect';

const MAX_SIZE = 100;
const MAX_FILE_SIZE = 15;
const EMPTY_RETAILERS: never[] = [];

const errorStyle = (errors: string[], keys: string[]) => {
    const isError = !!keys.filter((key: string) => errors?.includes(key)).length;

    return {
        boxShadow: `0 0 0 1px ${isError ? 'red' : 'blue.500'} !important`,
        borderColor: isError ? 'red !important' : 'inherit',
    };
};

export const usePicturesFilters = (
    initOptions: Partial<TPictures> = {},
    optionAll?: boolean,
    disabled?: boolean,
    validation?: (filters: Partial<TPictures>) => string[],
) => {
    const translate = useTranslation();
    const { data: resp, isLoading } = useGetRetailerByClientQuery();
    const retailers = resp?.data ?? EMPTY_RETAILERS;

    const [filters, setFilters] = useState<Partial<TPictures & { isAll: boolean }>>(initOptions || {});
    const [errors, setErrors] = useState<string[]>([]);

    const setMetaData = (key: keyof TPictures, value: any) => {
        setFilters((m) => {
            const newMeta = { ...m, [key]: value };

            if (isEmpty(value)) {
                delete newMeta[key];
            }

            if (key === 'retailers_id') {
                newMeta.isAll = value.length === retailers.length;
            }

            return newMeta;
        });
    };

    useEffect(() => {
        const retailersValue = initOptions.retailers_id;
        const isAll = !retailersValue || retailersValue?.length === 0;

        setFilters({
            ...initOptions,
            retailers_id: (isAll ? retailers?.map((r) => r.id) : retailersValue) || [],
            isAll,
        });
    }, [JSON.stringify(initOptions), retailers]);

    useEffect(() => {
        retailers && setErrors(validation?.(filters) || []);
    }, [filters]);

    const currentYear = new Date().getFullYear();

    const props = useMemo(
        () => ({
            setFilters,
            filters,
            errors,
            image_caractersitics: (
                <Select
                    bg="white"
                    my={2}
                    value={filters.image_caractersitics}
                    onChange={(e) => setMetaData('image_caractersitics', e.target.value)}
                    disabled={disabled}
                >
                    {optionAll && <option value="">{translate('pictures.upload.format.all')}</option>}
                    <option value="default">{translate('pictures.upload.format.default')}</option>
                    <option value="hero">{translate('pictures.upload.format.hero')}</option>
                    <option value="fop">{translate('pictures.upload.format.fop')}</option>
                </Select>
            ),
            image_type: (
                <Select
                    bg="white"
                    my={2}
                    value={filters.image_type}
                    onChange={(e) => setMetaData('image_type', e.target.value)}
                    disabled={disabled}
                >
                    {optionAll && <option value="">{translate('pictures.upload.format.all')}</option>}
                    <option value="primary">{translate('pictures.upload.type.primary')}</option>
                    <option value="secondary">{translate('pictures.upload.type.secondary')}</option>
                </Select>
            ),
            ean: <Input value={filters.ean} disabled my={2} />,
            duplicatePosition: (
                <Switch
                    my={2}
                    isChecked={!!filters.duplicatePosition}
                    onChange={(e) => setMetaData('duplicatePosition', e.target.checked)}
                />
            ),
            isFromApi: (
                <Select
                    bg="white"
                    my={2}
                    value={filters.isFromApi ? '0' : filters.isFromApi === false ? '1' : ''}
                    onChange={(e) => setMetaData('isFromApi', e.target.value ? e.target.value === '0' : e.target.value)}
                    disabled={disabled}
                >
                    <option value="">{translate('pictures.upload.format.all')}</option>
                    <option value="1">{translate('pictures.isManual.yes')}</option>
                    <option value="0">{translate('pictures.isManual.no')}</option>
                </Select>
            ),
            position: (
                <Input
                    {...errorStyle(errors, ['position', 'positionExist'])}
                    value={filters.position || ''}
                    onChange={(e) => setMetaData('position', e.target.value)}
                    my={2}
                    backgroundColor="white"
                    disabled={disabled}
                />
            ),
            year: (
                <Select
                    bg="white"
                    my={2}
                    value={filters.year}
                    onChange={(e) => setMetaData('year', e.target.value)}
                    disabled={disabled}
                >
                    {Array(4)
                        .fill('')
                        .map((_x, idx) => (
                            <option value={currentYear - idx} key={currentYear - idx}>
                                {currentYear - idx}
                            </option>
                        ))}
                </Select>
            ),
            overwrite: (
                <Select
                    bg="white"
                    my={2}
                    value={filters.overwrite ? '1' : '0'}
                    onChange={(e) => setMetaData('overwrite', e.target.value === '1')}
                    disabled={disabled}
                >
                    <option value="0">{translate('no')}</option>
                    <option value="1">{translate('yes')}</option>
                </Select>
            ),
            retailers_id: isLoading ? (
                <Spinner />
            ) : (
                <RSelect
                    setAll
                    options={formatOptionsForRSelect(retailers, 'name', 'id') || []}
                    selectedOptions={filters.retailers_id || []}
                    onChange={(selected: any[]) => setMetaData('retailers_id', selected)}
                    disabled={disabled}
                    styles={errorStyle(errors, ['retailers_id'])}
                />
            ),
        }),
        [retailers, JSON.stringify(filters), errors, disabled],
    );

    return props;
};

const formatData = (selectedFiles: TFile[], meta: Partial<TPictures>) => {
    const formData = new FormData();
    const { isAll, ...options } = { ...meta };

    if (isAll) {
        delete options.retailers_id;
    }

    if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
            formData.append('files', selectedFiles[i]);
        }

        // Put meta into formData
        Object.entries(options).forEach(([k, v]) => formData.append(k, `${v}`));
    }

    return formData;
};

const validation = (f: Partial<TPictures>) => {
    const errors: string[] = [];

    if (f.retailers_id?.length === 0) {
        errors.push('retailers_id');
    }

    return errors;
};

const useUploadSection = (disclosure: ReturnType<typeof useDisclosure>) => {
    const companyInfo = useGetCompanyInfoSelector();
    const [uploadPictures, { isLoading }] = useUploadPicturesMutation();
    const { onOpen, onClose } = disclosure;
    const { isOpen: isOverwriteOpen, onOpen: onOverwriteOpen, onClose: onOverwriteClose } = useDisclosure();
    const toast = useToast();
    const translate = useTranslation();

    const [buffer, setBuffer] = useState(0);

    const initMeta: Partial<TPictures & { upc_type?: string }> = {
        image_caractersitics: 'default',
        image_type: 'primary',
        company_id: companyInfo?.id,
        country_id: companyInfo?.country_id,
        upc_type: companyInfo?.upc_type,
        retailers_id: [],
    };

    const {
        filters: meta,
        image_caractersitics,
        image_type,
        overwrite,
        retailers_id,
    } = usePicturesFilters(initMeta, false, false, validation);

    const [filesToUpload, setFilesToUpload] = useState<TFile[]>([]);
    const [uploadedData, setUploadedData] = useState<TPictureUploadResponse['data']>([]);

    const handleFileChange = (files: TFile[] | null, removed?: boolean) => {
        if (files) {
            const filteredFiles = files.filter((file) => file.type.startsWith('image/'));

            if (removed) {
                setFilesToUpload(filteredFiles);
                return;
            }

            // keyBy + values to get uniq image name
            setFilesToUpload((f) => values(keyBy([...f, ...filteredFiles], (f) => f.name)));
        }
    };

    const onDone = (isError?: boolean) => {
        onClose();
        setBuffer(0);
        !isError && setFilesToUpload([]);
    };

    const apiCall = async (uploads: TFile[][], idx: number = 0) => {
        const upload = uploads[idx];

        idx && setBuffer((100 / uploads.length) * idx);

        if (!upload) {
            return;
        }

        try {
            const dataToSend = formatData(upload, meta);
            const result = await uploadPictures(dataToSend);

            if ('error' in result) {
                const errorObj = result.error;
                let message = translate('error.generic') as string;

                if (isFetchBaseQueryError(errorObj)) {
                    const data = errorObj.data;
                    if (isErrorWithMessage(data)) {
                        message = data.message;
                    } else if (typeof data === 'object' && data != null && 'error' in data) {
                        const errorValue = data.error;
                        if (typeof errorValue === 'string') {
                            message = errorValue;
                        }
                    }
                }

                toast({
                    title: message,
                    status: 'error',
                    isClosable: true,
                });
                onDone(true);
                return;
            }

            setUploadedData((ud) => [...ud, ...(result.data?.data || [])]);

            apiCall(uploads, idx + 1);
        } catch (err) {
            onDone(true);
        }
    };

    const handleUpload = async () => {
        if (meta.overwrite && !isOverwriteOpen) {
            onClose();
            onOverwriteOpen();
        } else {
            onOverwriteClose();
            onOpen();
            setUploadedData([]);

            const uploads: TFile[][] = [];
            let count = 0;
            let group = 0;

            filesToUpload.forEach((f: TFile) => {
                const fileSize = f.size / 1048576;

                if (fileSize > MAX_FILE_SIZE) {
                    setUploadedData((ud) => [...ud, { file: f, response: { error: 'FILE_TOO_LARGE' } }]);
                    return;
                }

                const size = count + fileSize;

                if (size >= MAX_SIZE) {
                    group++;
                }

                if (!uploads[group]) {
                    uploads[group] = [];
                }

                uploads[group].push(f);

                count = size >= MAX_SIZE ? fileSize : size;
            });

            await apiCall(uploads);
        }
    };

    useEffect(() => {
        if (uploadedData.length === filesToUpload.length) {
            onDone();
        }
    }, [uploadedData]);

    const props = {
        image_caractersitics,
        image_type,
        overwrite,
        retailers_id,
        filesToUpload,
        isLoading,
        uploadedData,
        buffer,
        isOverwriteOpen,
        onOverwriteClose,
        handleFileChange,
        handleUpload,
    };

    return props;
};

export default useUploadSection;
